import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact({ t, lang }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: t.contact.form.service_options[0],
    message: "",
  });
  const [status, setStatus] = useState("idle");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        message: form.message.trim(),
        locale: lang,
      });
      setStatus("success");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: t.contact.form.service_options[0],
        message: "",
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-slate-300 focus:border-[#0F2C29] outline-none py-3 text-[15px] text-[#0A0F1C] placeholder:text-slate-400 transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#F8F9FA] border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div
              className="text-[12px] tracking-[0.28em] uppercase text-[#475569] mb-4"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              — {t.contact.eyebrow}
            </div>
            <h2
              className="text-[36px] sm:text-[48px] leading-[1.05] tracking-tight text-[#0A0F1C] font-medium"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t.contact.title}
            </h2>
            <p
              className="mt-6 text-[17px] leading-relaxed text-[#475569] max-w-2xl"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {t.contact.desc}
            </p>
          </div>
        </div>

        {/* MAIN: large prominent form */}
        <div
          data-testid="contact-form-card"
          className="bg-white p-8 md:p-14 ring-1 ring-slate-200 shadow-[0_30px_80px_-30px_rgba(15,44,41,0.25)] relative"
        >
          <div className="absolute -top-3 left-8 md:left-14 inline-flex items-center gap-2 bg-[#0F2C29] text-white px-4 py-1.5 text-[10px] font-bold tracking-[0.22em] uppercase">
            <Sparkles className="w-3 h-3" strokeWidth={2} />
            {t.contact.form_highlight}
          </div>

          <p
            className="mt-2 mb-10 text-[15px] leading-relaxed text-[#475569] max-w-3xl"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {t.contact.form_subtitle}
          </p>

          <form
            onSubmit={submit}
            data-testid="inquiry-form"
            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#475569]">
                {t.contact.form.name}
              </label>
              <input
                data-testid="form-name"
                required
                type="text"
                value={form.name}
                onChange={onChange("name")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#475569]">
                {t.contact.form.phone}
              </label>
              <input
                data-testid="form-phone"
                required
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#475569]">
                {t.contact.form.email}
              </label>
              <input
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#475569]">
                {t.contact.form.service}
              </label>
              <select
                data-testid="form-service"
                value={form.service}
                onChange={onChange("service")}
                className={inputCls + " appearance-none cursor-pointer"}
              >
                {t.contact.form.service_options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] tracking-[0.22em] uppercase text-[#475569]">
                {t.contact.form.message}
              </label>
              <textarea
                data-testid="form-message"
                required
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                className={inputCls + " resize-none"}
              />
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-5 pt-4">
              <button
                type="submit"
                data-testid="form-submit"
                disabled={status === "sending"}
                className="group inline-flex items-center justify-center gap-3 bg-[#0F2C29] hover:bg-[#17423E] disabled:opacity-60 text-white text-[12px] font-bold tracking-[0.22em] uppercase px-10 py-5 transition-colors"
              >
                {status === "sending"
                  ? t.contact.form.submitting
                  : t.contact.form.submit}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </button>
              {status === "success" && (
                <div
                  data-testid="form-success"
                  className="text-[14px] text-[#0F2C29] font-medium"
                >
                  {t.contact.form.success}
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid="form-error"
                  className="text-[14px] text-red-600"
                >
                  {t.contact.form.error}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Secondary: direct contact (smaller, after the form) */}
        <div className="mt-16 pt-10 border-t border-slate-200">
          <div
            className="text-[12px] tracking-[0.28em] uppercase text-[#475569] mb-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {t.contact.direct_label}
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-[#475569]"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <a
              href="tel:+4746393022"
              data-testid="contact-phone-link"
              className="flex items-center gap-3 group text-[#0A0F1C]/70 hover:text-[#0F2C29] transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.6} />
              <span className="text-[14px]">46 393 022</span>
            </a>
            <a
              href="mailto:kontakt@fasadeakershus.no"
              data-testid="contact-email-link"
              className="flex items-center gap-3 group text-[#0A0F1C]/70 hover:text-[#0F2C29] transition-colors"
            >
              <Mail className="w-4 h-4" strokeWidth={1.6} />
              <span className="text-[14px] break-all">kontakt@fasadeakershus.no</span>
            </a>
            <div className="flex items-center gap-3 text-[#0A0F1C]/70">
              <MapPin className="w-4 h-4" strokeWidth={1.6} />
              <span className="text-[14px]">{t.contact.info.area}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
